import React from 'react';
import { ArrowRight, Star, Users, CheckCircle, TrendingUp, UserCheck, Clock, Zap, Cpu } from 'lucide-react';
import { Button } from '../ui/button';
import { heroContent, companyStats, quickTestimonials } from '../../data/mockData';

const HeroSection = ({ onBookDemo, onGetStarted }) => {
  const iconMap = {
    'trending-up': TrendingUp,
    'user-check': UserCheck,
    'clock': Clock,
    'zap': Zap,
    'cpu': Cpu
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-b from-white to-gray-50 overflow-hidden pt-20" aria-label="Hero section">
      {/* Background Effects */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-200/40 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        {/* Trust Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-4 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" aria-hidden="true" />
              <span className="text-gray-900 font-medium text-sm">{companyStats.rating}/5</span>
              <span className="text-gray-500 text-sm">on {companyStats.ratingSource}</span>
            </div>
            <div className="w-px h-4 bg-gray-200" aria-hidden="true" />
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4 text-gray-400" aria-hidden="true" />
              <span className="text-gray-500 text-sm">{companyStats.users} users</span>
            </div>
          </div>
        </div>

        {/* Main Headline - H1 for SEO */}
        <div className="text-center max-w-4xl mx-auto mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            {heroContent.headline}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {heroContent.subheadline}
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Button
            onClick={onBookDemo}
            size="lg"
            className="bg-gray-900 text-white hover:bg-gray-800 font-medium px-8 py-6 text-base rounded-xl group"
            aria-label="Book a demo to see AI AutoBots in action"
          >
            {heroContent.ctaPrimary}
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </Button>
          <Button
            onClick={onGetStarted}
            variant="outline"
            size="lg"
            className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-6 text-base rounded-xl"
            aria-label="Get started with AI AutoBots"
          >
            Get Started
          </Button>
        </div>

        {/* Features Quick List */}
        <ul className="flex flex-wrap justify-center gap-6 mb-16 list-none" aria-label="Key features">
          {[
            'Built-in automation that handles leads',
            'Voice, chat & website agents',
            'Ready to deploy on the go'
          ].map((feature, i) => (
            <li key={i} className="flex items-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>

        {/* Quick Testimonials */}
        <div className="text-center">
          <p className="text-gray-400 text-sm mb-8">Trusted by leading companies worldwide</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto" role="list" aria-label="Customer success metrics">
            {quickTestimonials.map((item) => {
              const Icon = iconMap[item.icon] || Zap;
              return (
                <div
                  key={item.id}
                  role="listitem"
                  className="flex flex-col items-center p-4 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-purple-600" aria-hidden="true" />
                  </div>
                  <p className="text-xs text-gray-600 text-center font-medium leading-tight">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
